import { GameConstants } from "../constants";
import { type ArmorDefinition } from "../definitions/items/armors";
import { type BackpackDefinition } from "../definitions/items/backpacks";
import { type ScopeDefinition } from "../definitions/items/scopes";

/*
    Spare gear (owner request of 16 October 2026, from a tester: "picking up duplicate items to prevent other players
    from getting it, like a good scope"). Besides what they wear, players carry one spare helmet, one spare vest and one
    spare backpack in their bag, and a second copy of each scope found as loot. The better piece is always the one worn:
    a spare gives nothing (no protection, no room, no perk) until the worn piece is dropped, when it is worn in its
    place. Masks aren't carried twice. The server (Inventory.gearPickup, Loot.canInteract) and the browser (the pickup
    prompt, the inventory screen) decide with these same functions.
*/

/** the gear that can be carried as a spare */
export type SpareKind = "helmet" | "vest" | "backpack";
export const SPARE_KINDS: readonly SpareKind[] = ["helmet", "vest", "backpack"];

export interface Spares {
    readonly helmet?: ArmorDefinition
    readonly vest?: ArmorDefinition
    readonly backpack?: BackpackDefinition
}

/**
 * What picking up a helmet, vest or backpack does: `wear` it (it beats what's worn), carry it as the `spare` (it
 * beats the spare, or there's none yet), or nothing: the spare (or the piece worn) is the `same` level, or `worse`
 */
export type GearPickup = "wear" | "spare" | "same" | "worse";

interface Levelled {
    readonly level: number
    readonly noDrop?: boolean
}

export function gearPickup(item: Levelled, worn: Levelled | undefined, spare: Levelled | undefined): GearPickup {
    const wornLevel = worn?.level ?? 0;
    if (item.level > wornLevel) return "wear";
    // nothing is carried beside a piece that can't be taken off (a werewolf's fur, the developer vest)
    if (worn?.noDrop !== true && (spare === undefined || item.level > spare.level)) return "spare";
    return item.level === (spare?.level ?? wornLevel) ? "same" : "worse";
}

/**
 * Interact takes the nearest thing in reach, a spare counting as this much farther away (in metres): on a pile, the gun
 * or the better vest comes first and the spare after, while standing on a scope a second copy of it still beats a
 * grenade at the edge of reach. The server (Player, InputActions.Interact) and the pickup prompt choose the same way
 */
export const SPARE_PICKUP_HANDICAP = 2;

/** the distance interact compares, squared as the callers have it: a spare's with {@link SPARE_PICKUP_HANDICAP} added */
export const pickupReach = (distanceSquared: number, spare: boolean): number =>
    spare ? (Math.sqrt(distanceSquared) + SPARE_PICKUP_HANDICAP) ** 2 : distanceSquared;

/** how many of this scope a player can carry: the default scopes, which everyone has, only once */
export function maxScopeCopies(scope: ScopeDefinition): number {
    return scope.giveByDefault === true || scope.noDrop === true ? 1 : GameConstants.player.maxScopeCopies;
}
